import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * MaintenanceOverlay - Affiche une page de maintenance
 * Props:
 * - enabled: Active/désactive l'overlay
 * - forceShow: Force l'affichage même en dehors des heures de maintenance (pour urgences)
 * 
 * Exemple d'utilisation:
 * - Maintenance programmée: <MaintenanceOverlay enabled={true} />
 * - Maintenance d'urgence: <MaintenanceOverlay enabled={true} forceShow={true} />
 */

type Props = {
  enabled: boolean;
  forceShow?: boolean; // Afficher la maintenance même en dehors de la fenêtre horaire
};

// Returns true if current time in Montreal is between 00:00 and 04:59
function isMaintenanceWindow(now: Date) {
  try {
    const hourStr = new Intl.DateTimeFormat('en-CA', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'America/Toronto', // Montreal timezone
    }).format(now);
    const hour = parseInt(hourStr, 10);
    return isWithinWindow(hour);
  } catch {
    // Fallback to client local time if Intl/timeZone failed
    const h = now.getHours();
    return isWithinWindow(h);
  }
}

/**
 * MODIFIER ICI: plage horaire de maintenance (heure de Montréal)
 * - Heures au format 0–23
 * - La fin est exclusive (ex.: 0–5 = de 00:00 à 04:59)
 */
const MAINTENANCE_START_HOUR = 10; // début (inclus)
const MAINTENANCE_END_HOUR = 15;   // fin (exclus)

function isWithinWindow(hour: number) {
  const start = clampHour(MAINTENANCE_START_HOUR);
  const end = clampHour(MAINTENANCE_END_HOUR);
  if (start === end) return false; // fenêtre vide
  if (start < end) return hour >= start && hour < end;
  // plage traversant minuit, ex.: 22 -> 2
  return hour >= start || hour < end;
}

function clampHour(h: number) {
  if (Number.isFinite(h)) {
    const x = Math.floor(h);
    if (x >= 0 && x <= 23) return x;
  }
  return 0;
}

function montrealTimeString(now: Date) {
  try {
    return new Intl.DateTimeFormat('fr-CA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Toronto',
    }).format(now);
  } catch {
    return now.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', hour12: false });
  }
}

export function MaintenanceOverlay({ enabled, forceShow = false }: Props) {
  const [now, setNow] = useState(() => new Date());

  const show = useMemo(() => enabled && (forceShow || isMaintenanceWindow(now)), [enabled, forceShow, now]);

  useEffect(() => {
    // Refresh every 30s to auto-hide/show at boundaries
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!show) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    // Prevent background scroll and layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [show]);

  if (!show) return null;

  const currentTime = montrealTimeString(now);
  const isForced = forceShow;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483646,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: 16,
        backgroundColor: '#000',
        color: '#fff',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={isForced ? "Maintenance d'urgence" : 'Maintenance nocturne'}
    >
      <div style={{ width: '100%', maxWidth: 576 }}>
        <div style={{
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 16,
          backgroundColor: '#18181b',
          padding: 24,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <span style={{
              display: 'inline-flex',
              width: 32,
              height: 32,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'rgba(245, 158, 11, 0.2)',
              color: '#fcd34d',
            }}>
              {/* Clock icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-.75 4.5a.75.75 0 0 1 1.5 0v5.19l3.03 3.03a.75.75 0 1 1-1.06 1.06l-3.22-3.22a.75.75 0 0 1-.22-.53V6.75Z" clipRule="evenodd" />
              </svg>
            </span>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
                {isForced ? 'Maintenance d\'urgence' : 'Maintenance nocturne'}
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: '#d4d4d8' }}>Heure de Montréal — {currentTime}</p>
            </div>
          </div>

          <p style={{ color: '#e4e4e7', lineHeight: 1.625, textAlign: 'center' }}>
            {isForced ? (
              <>
                Le site est temporairement indisponible pour des raisons techniques. 
                Nous travaillons à résoudre le problème dans les plus brefs délais.
              </>
            ) : (
              <>
                De 00h00 à 05h00 (heure de Montréal), le site effectue des mises à jour
                et synchronisations (joueurs, salaires, etc.). Durant cette période, l'accès est temporairement
                indisponible.
              </>
            )}
          </p>

          <div style={{
            marginTop: 24,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            padding: 16,
          }}>
            <p style={{ margin: 0, fontSize: 14, color: '#d4d4d8', textAlign: 'center' }}>
              {isForced ? (
                'Merci de revenir dans quelques minutes.'
              ) : (
                <>
                  Merci de revenir après <span style={{ fontWeight: 500, color: '#fcd34d' }}>05h00</span>.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MaintenanceOverlay;
